import { Injectable, Inject } from '@angular/core';
import { APP_BASE_HREF } from '@angular/common';

import { CookieService } from 'ngx-cookie-service';
import { ConfigModel } from '../models/config.model';
import { userAppPreferencesDefault } from '../../Users/data/user-app-preferences-default';
import { defaultChartColorsBurrellesLuce } from '../../Users/data/default-chart-colors-burrellesLuce';
import { defaultChartColorsKantar } from '../../Users/data/default-chart-colors-kantar';
import { defaultChartColorsLexisNexis } from '../../Users/data/default-chart-colors-lexisNexis';
import { defaultChartColorsNeptuneOnly } from '../../Users/data/default-chart-colors-neptune-only';
import { defaultChartColorsNewsDirect } from '../../Users/data/default-chart-colors-NewsDirect';
import { defaultChartColorsPaMedia } from '../../Users/data/default-chart-colors-PaMedia';
import { defaultChartColorsUnicepta } from '../../Users/data/default-chart-colors-Unicepta';
import { defaultSocialColorMap } from '../../Users/data/default-social-color-map';
import { socialNetworkTypeColorMap } from '../../Users/data/social-network-type-color-map';
import { chartColors } from '../../../Common/Charts/enums/chart-colors.enum';
import { PartnerResellers } from '../enums/partner-resellers.enum';

@Injectable()
export class ConfigService {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    private static readonly CONFIG_PATH = '/api/neptune/configuration';

    private static readonly BRAND_GUID_COOKIE = 'brandGUID'; // Cookie name for brandGUID

    public static readonly burrellesLuceGUID = 'd58b15fb-5604-4394-be91-fa68d2fd8415';
    public static readonly lexisNexisGUID = '96b3e718-12e4-4f9c-8379-c0a5520b912b';
    public static readonly kantar = 'dd4df5cc-7ece-44f5-8ab1-5f8c2b8a9588';
    public static readonly unicepta = 'fea749fb-d486-4f81-ae6d-ce0cac5e90c3';
    public static readonly newsDirect = '6bc67bb0-2ffc-4296-b6c5-738bbae4e214';
    public static readonly paMedia = '0c6572b7-86f6-4fa4-b170-13baa4f551e6';
    public static readonly refmedia = '6231242c-bf16-4fe2-a77f-2824ce61277c';

    public static readonly subdomainMap: Map<string, string> = new Map([
        // Burrelles is no longer a partner; use a Burrelles account instead
        // ['burrelles', ConfigService.burrellesLuceGUID],
        ['lexisnexismediacontacts', ConfigService.lexisNexisGUID],
        ['kantarmedia', ConfigService.kantar],
        ['unicepta', ConfigService.unicepta],
        ['newsdirect', ConfigService.newsDirect],
        ['mediapoint', ConfigService.paMedia],
        ['refmedia', ConfigService.refmedia],
        // local hard-wire
        // ['angular-agility', null],
    ]);

    public brandGUIDToPartnerReseller: Map<string, PartnerResellers> = new Map([
        [ConfigService.burrellesLuceGUID, PartnerResellers.burrelles],
        [ConfigService.lexisNexisGUID, PartnerResellers.lexisNexis],
        [ConfigService.newsDirect, PartnerResellers.newsdirect],
        [ConfigService.unicepta, PartnerResellers.unicepta],
        [ConfigService.paMedia, PartnerResellers.paMedia],
        [ConfigService.kantar, PartnerResellers.kantar],
        [ConfigService.refmedia, PartnerResellers.refmedia]
    ]);

    public accesswireMode: boolean  = false;
    private noRedirect: boolean = false;

    protected config: ConfigModel;

    // This is primarily used to deal with differences in how scrollbars render on Mac relative to other platforms
    // Specifically: scrollbars on mac are treated as an overlay of their content, whereas on other platforms they displace content
    public readonly isMac: boolean = window.navigator.platform.includes('MacIntel');
    public scrollbarTakesSpace: boolean = true; // default for windows

    constructor(
        protected cookieService: CookieService,
        @Inject(APP_BASE_HREF) protected appBaseHref: string,
        @Inject('DEPLOY_URL') protected deployUrl: string
    ) {
        if (this.isMac) {
            document.body.classList.add('agpr--mac');
            this.checkScrollbarWidth();
        }

        this.setConfig();
    }

    public checkScrollbarWidth() {
        const el = document.createElement('div');
        el.style.width = '100px';
        el.style.height = '100px';
        el.style.overflow = 'scroll';
        el.style.position = 'absolute';
        el.style.top = '-1000px';

        document.body.appendChild(el);
        this.scrollbarTakesSpace = el.offsetWidth - el.clientWidth > 0;
        document.body.removeChild(el);

        if (!this.scrollbarTakesSpace) {
            document.body.classList.add('agpr--scrollbarTakesNoSpace');
        }
    }

    public getConfig() {
        return this.config;
    }

    /**
     * Currently limited to specific accounts on test/preprod. No Prod access. Will be removed once fully integrated and tested
     */
    public accountHasAccessToThirdPartyPitchingOnEnvironment(accountId: number): boolean {
        //  Production has an empty array, so we want to allow all accounts
        return this.config.thirdPartyEnabledAccounts?.length === 0 || this.config?.thirdPartyEnabledAccounts.includes(accountId);
    }

    public getBrandGUID() {
        return this.config.brandGUID;
    }

    public getBrandGUIDCookie(): string {
        return this.cookieService.get(ConfigService.BRAND_GUID_COOKIE);
    }

    public getNoRedirect() {
        return this.noRedirect;
    }

    public setConfig() {
        if (this.config == null) {
            this.config = new ConfigModel();

            this.setBrandGUID();
        }
    }

    // Checks white-label status from domain only
    public isWhiteLabelDomain(): boolean {
        const domains = location.hostname.split('.');
        const whiteLabelDomains = Array.from(ConfigService.subdomainMap.keys());
        for (const domain of domains) {
            for (const whiteLabelDomain of whiteLabelDomains) {
                if (domain.indexOf(whiteLabelDomain) !== -1) {
                    return true;
                }
            }
        }
        return false;
    }

    // Checks white-label status from domain or cookie
    public isWhiteLabelBrand(): boolean {
        return this.isWhiteLabelDomain() || this.hasBrandGUID();
    }

    public setBrandGUID(): void {
        const domains = location.hostname.split('.');
        let brandGUID = null;
        const whiteLabelDomains = Array.from(ConfigService.subdomainMap.keys());
        for (const domain of domains) {
            for (const whiteLabelDomain of whiteLabelDomains) {
                // This checks to see if the string from the map is contained at all in the domain, for example,
                // neptune-phabricator-burrelles contains burrelles, so it would set the brandGUID for burrelles
                if (domain.indexOf(whiteLabelDomain) !== -1) {
                    brandGUID = ConfigService.subdomainMap.get(whiteLabelDomain);
                    break;
                }
            }
            if (brandGUID) {
                break;
            }
        }

        if (!brandGUID) {
            brandGUID = this.getBrandGUIDCookie();
        }

        this.config.brandGUID = brandGUID;

        const urlParams = new URLSearchParams(window.location.search);
        this.noRedirect = urlParams.get('noRedirect') === '';

        if (!this.noRedirect) {
            this.loadBrandStyles();
        }
        this.setDefaultChartColors();
    }

    public hasBrandGUID(): boolean {
        return this.config.brandGUID !== null && this.config.brandGUID !== undefined && this.config.brandGUID !== '';
    }

    public brandGUIDIsBurrelles(): boolean {
        return this.config.brandGUID === ConfigService.burrellesLuceGUID;
    }

    public brandGUIDIsLexisNexis(): boolean {
        return this.config.brandGUID === ConfigService.lexisNexisGUID;
    }

    public brandGUIDIsKantar(): boolean {
        return this.config.brandGUID === ConfigService.kantar;
    }

    public brandGUIDIsUnicepta(): boolean {
        return this.config.brandGUID === ConfigService.unicepta;
    }

    public brandGUIDIsNewsDirect(): boolean {
        return this.config.brandGUID === ConfigService.newsDirect;
    }

    public brandGUIDIsPaMedia(): boolean {
        return this.config.brandGUID === ConfigService.paMedia;
    }

    public brandGUIDIsRefMedia(): boolean {
        return this.config.brandGUID === ConfigService.refmedia;
    }

    public getPartnerReseller(): PartnerResellers {
        if (this.hasBrandGUID()) {
            return this.brandGUIDToPartnerReseller.get(this.config.brandGUID);
        } else {
            return null;
        }
    }

    private loadBrandStyles(): void {
        if (this.config.brandGUID) {
            const path = `${this.appBaseHref}${this.deployUrl}${this.config.brandGUID}.css`;
            const style = document.createElement('link');

            style.setAttribute('rel', 'stylesheet');
            style.setAttribute('type', 'text/css');
            style.setAttribute('href', path);

            document.head.appendChild(style);
            document.body.classList.add(`brand-${this.config.brandGUID}`);

            if (this.brandGUIDIsPaMedia()) {
                const font = document.createElement('link');
                font.setAttribute('rel', 'stylesheet');
                font.setAttribute('href', 'https://use.typekit.net/jos8xmc.css');
                document.head.appendChild(font);
            }
        }
    }

    private setDefaultChartColors(): void {
        // All color config settings should happen in this method
        // In other places, those const variables should not be modified

        if (this.config.brandGUID) {
            if (this.brandGUIDIsBurrelles()) {
                userAppPreferencesDefault.reportsPrefs.defaultChartColors = defaultChartColorsBurrellesLuce;
            }
            if (this.brandGUIDIsKantar()) {
                userAppPreferencesDefault.reportsPrefs.defaultChartColors = defaultChartColorsKantar;
            }
            if (this.brandGUIDIsLexisNexis()) {
                userAppPreferencesDefault.reportsPrefs.defaultChartColors = defaultChartColorsLexisNexis;
            }
            if (this.brandGUIDIsNewsDirect()) {
                userAppPreferencesDefault.reportsPrefs.defaultChartColors = defaultChartColorsNewsDirect;
            }
            if (this.brandGUIDIsPaMedia()) {
                userAppPreferencesDefault.reportsPrefs.defaultChartColors = defaultChartColorsPaMedia;
            }
            if (this.brandGUIDIsUnicepta()) {
                userAppPreferencesDefault.reportsPrefs.defaultChartColors = defaultChartColorsUnicepta;
            }
        } else if (this.isNewBranding()) {
            userAppPreferencesDefault.reportsPrefs.defaultChartColors = defaultChartColorsNeptuneOnly;
        }

        // update defaultSocialColorMap based on user preference default colors
        defaultSocialColorMap[0] = userAppPreferencesDefault.reportsPrefs.defaultChartColors.generalColors.color1 as chartColors;
        defaultSocialColorMap[1] = userAppPreferencesDefault.reportsPrefs.defaultChartColors.generalColors.color2 as chartColors;
        defaultSocialColorMap[2] = userAppPreferencesDefault.reportsPrefs.defaultChartColors.generalColors.color3 as chartColors;
        defaultSocialColorMap[3] = userAppPreferencesDefault.reportsPrefs.defaultChartColors.generalColors.color4 as chartColors;
        defaultSocialColorMap[4] = userAppPreferencesDefault.reportsPrefs.defaultChartColors.generalColors.color5 as chartColors;
        defaultSocialColorMap[5] = userAppPreferencesDefault.reportsPrefs.defaultChartColors.generalColors.color6 as chartColors;
        defaultSocialColorMap[6] = userAppPreferencesDefault.reportsPrefs.defaultChartColors.generalColors.color7 as chartColors;
        defaultSocialColorMap[7] = userAppPreferencesDefault.reportsPrefs.defaultChartColors.generalColors.color8 as chartColors;
        defaultSocialColorMap[8] = userAppPreferencesDefault.reportsPrefs.defaultChartColors.generalColors.color9 as chartColors;
        defaultSocialColorMap[9] = userAppPreferencesDefault.reportsPrefs.defaultChartColors.generalColors.color10 as chartColors;
        defaultSocialColorMap[10] = userAppPreferencesDefault.reportsPrefs.defaultChartColors.generalColors.color11 as chartColors;
        defaultSocialColorMap[11] = userAppPreferencesDefault.reportsPrefs.defaultChartColors.generalColors.color12 as chartColors;

        // update socialNetworkTypeColorMap based on user preference default colors
        socialNetworkTypeColorMap.TWEET = userAppPreferencesDefault.reportsPrefs.defaultChartColors.socialNetworkColors.twitter as chartColors;
        socialNetworkTypeColorMap.TWITTER = userAppPreferencesDefault.reportsPrefs.defaultChartColors.socialNetworkColors.twitter as chartColors;
        socialNetworkTypeColorMap.TWITTER_LABEL = userAppPreferencesDefault.reportsPrefs.defaultChartColors.otherColors.textColor as chartColors;
        socialNetworkTypeColorMap.FACEBOOK = userAppPreferencesDefault.reportsPrefs.defaultChartColors.socialNetworkColors.facebook as chartColors;
        socialNetworkTypeColorMap.FACEBOOK_LABEL = userAppPreferencesDefault.reportsPrefs.defaultChartColors.otherColors.textColor as chartColors;
        socialNetworkTypeColorMap.INSTAGRAM = userAppPreferencesDefault.reportsPrefs.defaultChartColors.socialNetworkColors.instagram as chartColors;
        socialNetworkTypeColorMap.INSTAGRAM_LABEL = userAppPreferencesDefault.reportsPrefs.defaultChartColors.otherColors.textColor as chartColors;
        socialNetworkTypeColorMap.YOUTUBE = userAppPreferencesDefault.reportsPrefs.defaultChartColors.socialNetworkColors.youtube as chartColors;
        socialNetworkTypeColorMap.YOUTUBE_LABEL = userAppPreferencesDefault.reportsPrefs.defaultChartColors.otherColors.textColor as chartColors;
        socialNetworkTypeColorMap.REDDIT = userAppPreferencesDefault.reportsPrefs.defaultChartColors.socialNetworkColors.reddit as chartColors;
        socialNetworkTypeColorMap.REDDIT_LABEL = userAppPreferencesDefault.reportsPrefs.defaultChartColors.otherColors.textColor as chartColors;
        socialNetworkTypeColorMap.TIKTOK = userAppPreferencesDefault.reportsPrefs.defaultChartColors.socialNetworkColors.tiktok as chartColors;
        socialNetworkTypeColorMap.TIKTOK_LABEL = userAppPreferencesDefault.reportsPrefs.defaultChartColors.otherColors.textColor as chartColors;
        socialNetworkTypeColorMap.LINKEDIN = userAppPreferencesDefault.reportsPrefs.defaultChartColors.socialNetworkColors.linkedin as chartColors;
        socialNetworkTypeColorMap.LINKEDIN_LABEL = userAppPreferencesDefault.reportsPrefs.defaultChartColors.otherColors.textColor as chartColors;
        socialNetworkTypeColorMap.BLUESKY = userAppPreferencesDefault.reportsPrefs.defaultChartColors.socialNetworkColors.bluesky as chartColors;
        socialNetworkTypeColorMap.BLUESKY_LABEL = userAppPreferencesDefault.reportsPrefs.defaultChartColors.otherColors.textColor as chartColors;
    }

    public isNewBranding(): boolean {
        return this.config.brandingRefresh;
    }
}
